import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Section, Eyebrow } from "@/components/Bits";
import ArrowLink from "@/components/ArrowLink";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import Marquee from "@/components/Marquee";
import { IMAGES } from "@/data/site";

const CHAPTERS = [
  {
    no: "01",
    title: "Home education has its own rhythm.",
    body: [
      "Different families work in different ways. Some follow structured plans, others learn through projects, interests and everyday life — and many use a mixture of everything.",
      "Our aim is not to prescribe how home education should look. Instead, we build flexible tools that families can shape around the way they already learn.",
    ],
  },
  {
    no: "02",
    title: "Created around real needs.",
    body: [
      "FreeRange.Education is developed from first-hand experience of home education.",
      'Many of our ideas begin with a simple question: “Why isn\u2019t there a tool that does this the way we need it to?” When the right tool doesn\u2019t exist, we explore building it.',
    ],
  },
];

export default function About() {
  return (
    <>
      <Seo title="About | FreeRange.Education" description="FreeRange.Education builds practical, flexible tools for home-educating families, shaped around first-hand experience of home education." />

      <Section className="pb-16 pt-32 md:pt-40">
        <Reveal>
          <Eyebrow>About</Eyebrow>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-charcoal sm:text-6xl lg:text-7xl">
            About FreeRange.Education
          </h1>
        </Reveal>
        <div className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <Reveal delay={0.1}>
            <div className="space-y-5 text-lg leading-relaxed text-charcoal/80">
              <p>
                FreeRange.Education exists because the tools designed for schools do not always fit the way
                home-educating families actually learn.
              </p>
              <p>
                Our products are built from first-hand experience of home education and around practical needs we
                have encountered ourselves.
              </p>
              <p>
                We believe technology can make planning, practice and organisation easier without forcing home
                education into school-shaped systems.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="group">
              <ImagePlaceholder
                src={IMAGES.readingTogether}
                alt="A family learning together at home"
                ratio="portrait"
                rounded="rounded-[2rem]"
                testid="about-image"
                className="border-4 border-white shadow-solid-purple"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <Marquee text="Learning begins at home" separator="✳" />

      <Section className="py-24 md:py-32">
        <Reveal className="mb-14">
          <Eyebrow color="text-turquoise">Why FreeRange?</Eyebrow>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight text-charcoal sm:text-5xl">
            A short manifesto for tools that fit real life.
          </h2>
        </Reveal>

        <Stagger className="space-y-14">
          {CHAPTERS.map((c) => (
            <StaggerItem key={c.no}>
              <div className="grid gap-6 border-t-2 border-charcoal/10 pt-8 md:grid-cols-[auto_1fr] md:gap-12">
                <span className="font-display text-6xl font-extrabold leading-none text-turquoise md:text-8xl">
                  {c.no}
                </span>
                <div className="max-w-2xl">
                  <h3 className="font-display text-2xl font-bold leading-tight tracking-tight text-charcoal sm:text-4xl">
                    {c.title}
                  </h3>
                  <div className="mt-4 space-y-4 text-lg leading-relaxed text-charcoal/75">
                    {c.body.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      <Section className="pb-28">
        <Reveal>
          <div className="flex flex-col items-start gap-6 rounded-[2rem] bg-purple p-10 text-cream md:flex-row md:items-center md:justify-between md:p-14">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                See what we're building.
              </h2>
              <p className="mt-3 max-w-lg text-lg text-cream/80">
                Two apps designed specifically for home education — both in active development.
              </p>
            </div>
            <ArrowLink to="/apps" variant="turquoise" testid="about-cta-apps">Explore our apps</ArrowLink>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
